import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Plus, AlertCircle, CheckCircle, ChevronLeft, ChevronRight,
  UserCog, Layers, Eye, UserPlus, Trash2,
} from "lucide-react";
import { ThemeContext }    from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import AlertModal          from "../../components/AlertModal";
import { useAuth }         from "../../context/AuthContext";

import { useAdminLevels }     from "./hooks/useAdminLevels";
import { useVisibilityRules } from "./hooks/useVisibilityRules";
import { useAssignEmployees } from "./hooks/useAssignEmployees";
import { useUserPermissionConfig } from "./hooks/useUserPermissionConfig";
import { useSharedLevelConfig }    from "./hooks/useSharedLevelConfig";

import { translations }        from "./constants/translations";
import { getStyles }           from "./styles/adminLevelStyles";
import { getTrimmedCompanyContext } from "./utils/encodeUtils";
import { getCompaniesForClient, safeUserLabel }  from "./utils/adminLevelUtils";

import CompanySelector    from "./components/CompanySelector";
import CreateLevelModal   from "./components/modals/CreateLevelModal";
import VisibilityModal    from "./components/modals/VisibilityModal";
import AssignEmployeesModal from "./components/modals/AssignEmployeesModal";
import ConfirmPersonalModal from "./components/modals/ConfirmPersonalModal";
import RemoveLevelModal     from "./components/modals/RemoveLevelModal";
import WelcomeHeader      from "./components/WelcomeHeader";
import UserPicker         from "./components/UserPicker";
import PageMatrix         from "./components/PageMatrix";
import PreviewPanel       from "./components/PreviewPanel";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

/**
 * Admin Level Configuration — orchestrator.
 * Two view modes, both using the same 3-tier editor (PageMatrix, instant save):
 *  - "users"  (default): per-user personal configs
 *                        (UserPicker → PageMatrix → PreviewPanel)
 *  - "levels": shared named levels — same editor bound to a level, plus
 *              level CRUD, employee assignment and visibility scoping
 */
const AdminLevelConfigurationPage = () => {
  const { theme: currentTheme } = useContext(ThemeContext);
  const theme    = currentTheme === "dark" ? "dark" : "light";
  const { language } = useContext(LanguageContext);
  const t        = translations[language];
  const styles   = getStyles(theme, language);

  // ── Admin-level feature gating (registry page "adminLevels") — applies to
  // the legacy Levels mode's mutating controls only. isFeatureVisible
  // bypasses for admin/superadmin, so full admins are unaffected.
  const { isFeatureVisible } = useAuth();
  const canAddLevel    = isFeatureVisible("adminLevels", "add");
  const canEditLevels  = isFeatureVisible("adminLevels", "edit");
  const canRemoveLevels = isFeatureVisible("adminLevels", "remove");

  // ── view mode ──
  const [viewMode, setViewMode] = useState("users"); // "users" | "levels"

  // ── responsive: three panels stack vertically under ~1100px ──
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1100 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1100);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── modal visibility ──
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showAssignModal,  setShowAssignModal]  = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ── local UI state (levels flow) ──
  const [sidebarCollapsed,       setSidebarCollapsed]       = useState(false);
  const [formData,               setFormData]               = useState({ name: "", description: "" });
  const [deletingLevelId,        setDeletingLevelId]        = useState(null);

  // ── per-user editor: pending confirmation before cloning a shared level ──
  const [pendingUser, setPendingUser] = useState(null);
  // ── per-user editor: confirm before unassigning the selected user's level ──
  const [showRemoveLevelModal, setShowRemoveLevelModal] = useState(false);

  // ── hooks ──
  const levelsHook = useAdminLevels();
  const {
    adminLevels, companies, selectedCompany, setSelectedCompany,
    companiesLoading, loading, error, success,
    fetchAdminLevels, createAdminLevel, deleteAdminLevel,
  } = levelsHook;

  const userConfig = useUserPermissionConfig(selectedCompany);
  const sharedConfig = useSharedLevelConfig(selectedCompany);

  // Levels tab only: "Configurations" is a superadmin-only surface (company
  // settings, leave/overtime rules, module toggles) — it isn't something a
  // shared admin level should ever be able to grant, so it's dropped from
  // the matrix here. The Users tab keeps the full registry unchanged.
  const levelsRegistry = useMemo(() => {
    if (!userConfig.registry) return userConfig.registry;
    return {
      ...userConfig.registry,
      pages: (userConfig.registry.pages || []).filter((p) => p.key !== "configurations"),
    };
  }, [userConfig.registry]);

  // ── initial page skeleton — only until companies finish their first load ──
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!companiesLoading) setPageReady(true);
  }, [companiesLoading]);

  const visibilityHook = useVisibilityRules(
    selectedCompany,
    (msg) => levelsHook.setSuccess?.(msg),
    (msg) => levelsHook.setError?.(msg)
  );

  const assignHook = useAssignEmployees(
    selectedCompany,
    (msg) => levelsHook.setSuccess?.(msg),
    (msg) => levelsHook.setError?.(msg),
    () => {
      const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
      fetchAdminLevels(client, company);
      // keep the Users tab's "Inherits <level>" badges in sync with the new assignment
      userConfig.fetchUsers(client, company);
    }
  );

  // ── helpers ──
  const allCompanies = getCompaniesForClient(companies, selectedCompany.client);

  const loadCompanyData = () => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    if (!client || !company) return;
    fetchAdminLevels(client, company);   // Levels tab
    userConfig.fetchUsers(client, company); // Users tab
    userConfig.clearSelectedUser();
    sharedConfig.clearSelectedLevel();
  };

  // ── per-user editor handlers ──
  const handlePickUser = (user) => {
    if (userConfig.selectedUser?.id === user.id) return;
    const info = userConfig.userLevelMap[user.id];
    // inheriting a shared level → confirm before cloning it into a personal one
    if (info && !info.isPersonal) setPendingUser(user);
    else userConfig.selectUser(user);
  };

  const handleConfirmPersonal = () => {
    const user = pendingUser;
    setPendingUser(null);
    if (user) userConfig.selectUser(user);
  };

  // selected user currently has a level (shared badge OR a loaded personal one)
  const selectedUserHasLevel = !!(
    userConfig.selectedUser &&
    (userConfig.userLevelMap[userConfig.selectedUser.id] || userConfig.personalLevel)
  );

  const handleRemoveLevel = async () => {
    setShowRemoveLevelModal(false);
    await userConfig.removeAdminLevel();
  };

  // ── levels handlers ──
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const ok = await createAdminLevel(formData);
    if (ok) { setShowCreateModal(false); setFormData({ name: "", description: "" }); }
  };

  const handleDeleteClick = (levelId) => { setDeletingLevelId(levelId); setShowDeleteConfirm(true); };
  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    if (deletingLevelId === sharedConfig.selectedLevel?.id) sharedConfig.clearSelectedLevel();
    await deleteAdminLevel(deletingLevelId);
    setDeletingLevelId(null);
  };

  const handleOpenVisibility = async (level) => {
    setShowVisibilityModal(true);
    await visibilityHook.open(level);
  };

  // Users tab: visibility rules live on the (lazily created) personal level
  const handleOpenUserVisibility = async () => {
    try {
      const levelId = await userConfig.ensurePersonalLevel();
      setShowVisibilityModal(true);
      await visibilityHook.open({ id: levelId });
    } catch (err) {
      userConfig.setError(err.message || t.personalLevelFailed);
    }
  };

  const handleSaveVisibility = async () => {
    const ok = await visibilityHook.save(() => {
      const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
      fetchAdminLevels(client, company);
    });
    if (ok) setShowVisibilityModal(false);
  };

  const handleOpenAssign = async (level) => {
    setShowAssignModal(true);
    await assignHook.open(level);
  };

  const handleSaveAssign = async () => {
    const ok = await assignHook.save();
    if (ok) setShowAssignModal(false);
  };

  // merged toasts (all hooks share one alert strip)
  const anyError   = error   || userConfig.error   || sharedConfig.error;
  const anySuccess = success || userConfig.success || sharedConfig.success;

  // pill-style header action button (Visibility / Assign / Delete)
  const actionPill = (Icon, label, tone, onClick) => {
    const tones = {
      blue:  { bg: theme === "dark" ? "rgba(46,124,186,0.14)"  : "rgba(46,124,186,0.08)",
               bgHover: theme === "dark" ? "rgba(46,124,186,0.28)"  : "rgba(46,124,186,0.16)",
               border: theme === "dark" ? "rgba(46,124,186,0.35)"  : "rgba(46,124,186,0.25)",
               color: theme === "dark" ? "#60a5fa" : "#2e7cba" },
      green: { bg: theme === "dark" ? "rgba(16,185,129,0.14)"  : "rgba(16,185,129,0.08)",
               bgHover: theme === "dark" ? "rgba(16,185,129,0.28)"  : "rgba(16,185,129,0.16)",
               border: theme === "dark" ? "rgba(16,185,129,0.35)"  : "rgba(16,185,129,0.25)",
               color: theme === "dark" ? "#34d399" : "#059669" },
      red:   { bg: theme === "dark" ? "rgba(239,68,68,0.14)"   : "rgba(239,68,68,0.08)",
               bgHover: theme === "dark" ? "rgba(239,68,68,0.28)"   : "rgba(239,68,68,0.15)",
               border: theme === "dark" ? "rgba(239,68,68,0.3)"    : "rgba(239,68,68,0.2)",
               color: theme === "dark" ? "#f87171" : "#dc2626" },
    };
    const c = tones[tone] || tones.blue;
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          fontSize: "11.5px", fontWeight: 600, padding: "4px 12px",
          borderRadius: "999px", cursor: "pointer", fontFamily: "inherit",
          transition: "all 0.18s ease",
          background: c.bg, border: `1px solid ${c.border}`, color: c.color,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = c.bgHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = c.bg; }}
      >
        <Icon size={13} />
        {label}
      </button>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until companies finish their first load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && companiesLoading) {
    const isRTL = language === "ar";
    return (
      <div>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل إعدادات المستويات الإدارية..." : "Loading admin level configuration..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Welcome header skeleton — the real header uses a negative marginTop
            to tuck under the topbar; zero it out here so it doesn't slide up
            over the LoadingPill above it. */}
        <div style={{ ...styles.modernCard, marginTop: 0 }} className="admin-card">
          <SkeletonBox width="260px" height="28px" style={{ marginBottom: "12px" }} />
          <SkeletonBox width="360px" height="16px" />
        </div>

        {/* Company selector skeleton */}
        <div style={{ ...styles.modernCard }} className="admin-card">
          <SkeletonBox width="160px" height="20px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: "16px" }}>
            <SkeletonBox width="100%" height="44px" style={{ borderRadius: "12px" }} />
            <SkeletonBox width="100%" height="44px" style={{ borderRadius: "12px" }} />
          </div>
        </div>

        {/* Editor panels skeleton */}
        <div style={{ ...styles.modernCard }} className="admin-card">
          <SkeletonBox width="200px" height="22px" style={{ marginBottom: "24px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 300px", gap: "24px", minHeight: "400px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {[1, 2, 3].map((i) => (
                <SkeletonBox key={i} width="100%" height="60px" style={{ borderRadius: "12px" }} />
              ))}
            </div>
            <SkeletonBox width="100%" height="100%" style={{ borderRadius: "12px", minHeight: "400px" }} />
            <SkeletonBox width="100%" height="100%" style={{ borderRadius: "12px", minHeight: "400px" }} />
          </div>
        </div>
      </div>
    );
  }

  // ── shared bits ──
  const viewToggle = (
    <div style={{
      display: "inline-flex", padding: "4px", gap: "3px", borderRadius: "14px",
      background: theme === "dark" ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.05)",
      border: theme === "dark"
        ? "1px solid rgba(148,163,184,0.15)"
        : "1px solid rgba(15,23,42,0.06)",
    }}>
      {[
        { mode: "users", label: t.usersTab, Icon: UserCog },
        { mode: "levels", label: t.levelsTab, Icon: Layers },
      ].map(({ mode, label, Icon }) => {
        const active = viewMode === mode;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => setViewMode(mode)}
            style={{
              display: "inline-flex", alignItems: "center", gap: "7px",
              padding: "9px 18px", borderRadius: "11px", border: "none",
              cursor: "pointer", fontSize: "13.5px", fontWeight: active ? 700 : 500,
              fontFamily: "inherit", transition: "all 0.22s ease",
              color: active ? "#ffffff" : (theme === "dark" ? "#94a3b8" : "#64748b"),
              background: active ? "#2e7cba" : "transparent",
              boxShadow: active ? "0 4px 12px rgba(46,124,186,0.35)" : "none",
            }}
          >
            <Icon size={15} />
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      {/* Welcome Header */}
      <WelcomeHeader
        title={t.welcomeTitle}
        subtitle={t.welcomeSubtitle}
        infoTitle={t.infoModalTitle}
        infoDesc={t.infoModalDesc}
        theme={theme}
        language={language}
      />

      {/* Company Selector Section */}
      <div style={{ ...styles.modernCard }} className="admin-card">
        <h3 style={styles.cardTitle}>{t.selectCompany}</h3>
        <CompanySelector
          t={t} styles={styles} theme={theme} language={language}
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          companies={companies}
          companiesLoading={companiesLoading}
          onLoad={loadCompanyData}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "20px" }}>
          <button
            style={{ ...styles.button, ...styles.primaryButton, flex: 1, justifyContent: "center" }}
            onClick={loadCompanyData}
            disabled={!String(selectedCompany.client || "").trim() || !String(selectedCompany.company || "").trim() || companiesLoading}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(46, 124, 186, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {companiesLoading ? t.loadingCompanies : t.loadDrive}
          </button>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "50px",
            height: "40px",
            backgroundColor: theme === "dark" ? "rgba(46, 124, 186, 0.2)" : "rgba(46, 124, 186, 0.1)",
            border: `2px solid ${theme === "dark" ? "rgba(46, 124, 186, 0.4)" : "rgba(46, 124, 186, 0.3)"}`,
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            color: theme === "dark" ? "#2E7CBA" : "#1E5A96",
            transition: "all 0.3s ease"
          }}>
            {viewMode === "users" ? userConfig.users.length : adminLevels.length}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {anyError   && <div style={{ ...styles.alert, ...styles.alertError   }}><AlertCircle size={20} /> {anyError}</div>}
      {anySuccess && <div style={{ ...styles.alert, ...styles.alertSuccess }}><CheckCircle size={20} /> {anySuccess}</div>}

      {/* ═══════════════ Main editor card ═══════════════ */}
      <div style={{ ...styles.modernCard }} className="admin-card">
        {/* Header: title + view mode toggle */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "14px",
          marginBottom: "28px", paddingBottom: "20px",
          borderBottom: `2px solid ${theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"}`,
        }}>
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>
              {viewMode === "users" ? t.pagesTitle : t.adminLevels}
            </h3>
            <p style={{ fontSize: "13px", color: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)", marginTop: "4px" }}>
              {viewMode === "users"
                ? `${userConfig.users.length} ${t.usersCount}`
                : `${adminLevels.length} ${adminLevels.length === 1 ? "level" : "levels"}`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {viewToggle}
            {viewMode === "levels" && canAddLevel && (
              <button
                style={{ ...styles.button, ...styles.primaryButton, display: "flex", alignItems: "center", gap: "8px" }}
                onClick={() => {
                  if (!selectedCompany.client || !selectedCompany.company) return;
                  setShowCreateModal(true);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(46, 124, 186, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Plus size={18} /> {t.createAdminLevel}
              </button>
            )}
          </div>
        </div>

        {/* ─────────── USERS MODE: three-panel master–detail ─────────── */}
        {viewMode === "users" && (
          <div style={{
            display: "grid",
gridTemplateColumns: isNarrow ? "1fr" : "360px minmax(0,1fr) 320px",            gap: "24px",
            alignItems: "start",
          }}>
            {/* 1 — user picker */}
            <div style={{
              backgroundColor: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
              borderRadius: "16px",
              padding: "16px",
              border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
            }}>
              <UserPicker
                users={userConfig.users}
                usersLoading={userConfig.usersLoading}
                usersLoaded={userConfig.usersLoaded}
                userLevelMap={userConfig.userLevelMap}
                selectedUserId={userConfig.selectedUser?.id}
                onPickUser={handlePickUser}
                theme={theme} language={language} t={t}
              />
            </div>

            {/* 2 — page/feature/column matrix */}
            <div style={{
              backgroundColor: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
              borderRadius: "16px",
              padding: "20px",
              border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
              minHeight: "360px",
            }}>
              <PageMatrix
                registry={userConfig.registry}
                hasTarget={!!userConfig.selectedUser}
                levelLoading={userConfig.levelLoading || userConfig.registryLoading}
                pageStates={userConfig.pageStates}
                features={userConfig.features}
                hiddenColumns={userConfig.hiddenColumns}
                onSetPageState={userConfig.setPageState}
                onToggleFeature={userConfig.toggleFeature}
                onToggleColumn={userConfig.toggleColumn}
                canRemoveLevel={selectedUserHasLevel}
                onRemoveLevel={() => setShowRemoveLevelModal(true)}
                headerActions={actionPill(Eye, t.visibilityControl, "blue", handleOpenUserVisibility)}
                theme={theme} language={language} t={t}
                targetLabel={userConfig.selectedUser ? safeUserLabel(userConfig.selectedUser) : ""}
              />
            </div>

            {/* 3 — live preview (only meaningful once a user is selected) */}
            <div style={{
              backgroundColor: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
              borderRadius: "16px",
              padding: "16px",
              border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
              opacity: userConfig.selectedUser ? 1 : 0.5,
              transition: "opacity 0.25s ease",
            }}>
              <PreviewPanel
                registry={userConfig.registry}
                pageStates={userConfig.pageStates}
                theme={theme} language={language} t={t}
              />
            </div>
          </div>
        )}

        {/* ─────────── LEVELS MODE: shared levels, same 3-tier editor ─────────── */}
        {viewMode === "levels" && (
          loading
            ? <div style={styles.loadingState}>{t.loading}</div>
            : adminLevels.length === 0
              ? <div style={styles.emptyState}>{t.noAdminLevels}</div>
              : (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: isNarrow
                      ? "1fr"
                      : `${sidebarCollapsed ? "auto" : "280px"} minmax(0,1fr) 320px`,
                    gap: "24px",
                    alignItems: "start",
                  }}>
                    {/* 1 — levels list */}
                    <div style={{
                      backgroundColor: theme === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
                      borderRadius: "16px",
                      padding: sidebarCollapsed ? "12px" : "16px",
                      border: `1px solid ${theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)"}`,
                      overflowY: "auto",
                      maxHeight: "600px",
                      transition: "all 0.3s ease",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      WebkitOverflowScrolling: "touch"
                    }}>
                      {/* Collapse Toggle Button */}
                      <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          padding: "8px",
                          marginBottom: "12px",
                          borderRadius: "8px",
                          border: `1px solid ${theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                          backgroundColor: theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
                          cursor: "pointer",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)";
                        }}
                      >
                        {sidebarCollapsed ? <ChevronRight size={18} style={{ color: theme === "dark" ? "#ddd" : "#333" }} /> : <ChevronLeft size={18} style={{ color: theme === "dark" ? "#ddd" : "#333" }} />}
                      </button>

                      {adminLevels.map((level) => {
                        const isSelected = sharedConfig.selectedLevel?.id === level.id;
                        return (
                        <button
                          key={level.id}
                          style={{
                            display: "flex",
                            flexDirection: sidebarCollapsed ? "row" : "column",
                            alignItems: "center",
                            justifyContent: sidebarCollapsed ? "center" : "flex-start",
                            width: "100%",
                            padding: sidebarCollapsed ? "10px" : "14px 12px",
                            marginBottom: "10px",
                            borderRadius: "10px",
                            border: isSelected ? `2px solid #2E7CBA` : `1px solid ${theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                            backgroundColor: isSelected ? "rgba(46, 124, 186, 0.2)" : theme === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.5)",
                            color: isSelected ? "#2E7CBA" : theme === "dark" ? "#ddd" : "#333",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: language === "ar" ? "right" : "left"
                          }}
                          onClick={() => (isSelected ? sharedConfig.clearSelectedLevel() : sharedConfig.selectLevel(level))}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.8)";
                              e.currentTarget.style.transform = "translateX(" + (language === "ar" ? "-2px" : "2px") + ")";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.5)";
                              e.currentTarget.style.transform = "translateX(0)";
                            }
                          }}
                          title={sidebarCollapsed ? level.name : ""}
                        >
                          {!sidebarCollapsed ? (
                            <>
                              <div style={{ fontWeight: isSelected ? "700" : "600", fontSize: "15px", marginBottom: "6px" }}>
                                {level.name}
                              </div>
                              <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "6px", lineHeight: "1.4" }}>
                                {level.description || "No description"}
                              </div>
                              <div style={{
                                fontSize: "11px",
                                backgroundColor: theme === "dark" ? "rgba(46, 124, 186, 0.3)" : "rgba(46, 124, 186, 0.1)",
                                color: "#2E7CBA",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                display: "inline-block",
                                fontWeight: "600"
                              }}>
                                {(level.users || []).length} {t.assignedUsersCount}
                              </div>
                            </>
                          ) : (
                            <div style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              color: isSelected ? "#2E7CBA" : theme === "dark" ? "#ddd" : "#333"
                            }}>
                              {level.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </button>
                        );
                      })}
                    </div>

                    {/* 2 — page/feature/column matrix (same editor as Users tab) */}
                    <div style={{
                      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                      borderRadius: "16px",
                      padding: "20px",
                      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
                      minHeight: "360px",
                    }}>
                      <PageMatrix
                        registry={levelsRegistry}
                        hasTarget={!!sharedConfig.selectedLevel}
                        levelLoading={sharedConfig.levelLoading || userConfig.registryLoading}
                        pageStates={sharedConfig.pageStates}
                        features={sharedConfig.features}
                        hiddenColumns={sharedConfig.hiddenColumns}
                        onSetPageState={canEditLevels ? sharedConfig.setPageState : () => {}}
                        onToggleFeature={canEditLevels ? sharedConfig.toggleFeature : () => {}}
                        onToggleColumn={canEditLevels ? sharedConfig.toggleColumn : () => {}}
                        disabled={!canEditLevels}
                        headerActions={
                          <>
                            {canEditLevels && actionPill(Eye, t.visibilityControl, "blue",
                              () => handleOpenVisibility(sharedConfig.selectedLevel))}
                            {canEditLevels && actionPill(UserPlus, t.assignEmployees, "green",
                              () => handleOpenAssign(sharedConfig.selectedLevel))}
                            {canRemoveLevels && actionPill(Trash2, t.deleteLevelBtn, "red",
                              () => handleDeleteClick(sharedConfig.selectedLevel.id))}
                          </>
                        }
                        emptyTitle={t.selectLevelPrompt}
                        emptyHint={t.selectLevelHint}
                        theme={theme} language={language} t={t}
                        targetLabel={sharedConfig.selectedLevel?.name || ""}
                      />
                    </div>

                    {/* 3 — live preview */}
                    <div style={{
                      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                      borderRadius: "16px",
                      padding: "16px",
                      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
                      opacity: sharedConfig.selectedLevel ? 1 : 0.5,
                      transition: "opacity 0.25s ease",
                    }}>
                      <PreviewPanel
                        registry={userConfig.registry}
                        pageStates={sharedConfig.pageStates}
                        theme={theme} language={language} t={t}
                      />
                    </div>
                  </div>
                )
        )}
      </div>

      {/* ── per-user: confirm cloning a shared level ── */}
      {pendingUser && (
        <ConfirmPersonalModal
          user={pendingUser}
          levelName={userConfig.userLevelMap[pendingUser.id]?.levelName || ""}
          onConfirm={handleConfirmPersonal}
          onClose={() => setPendingUser(null)}
          styles={styles} theme={theme} t={t}
        />
      )}

      {/* ── per-user: confirm unassigning the selected user's admin level ── */}
      {showRemoveLevelModal && userConfig.selectedUser && (
        <RemoveLevelModal
          user={userConfig.selectedUser}
          onConfirm={handleRemoveLevel}
          onClose={() => setShowRemoveLevelModal(false)}
          styles={styles} theme={theme} t={t}
        />
      )}

      {/* Modals (levels flow) */}
      {showCreateModal && (
        <CreateLevelModal
          t={t} styles={styles} formData={formData} setFormData={setFormData}
          onSubmit={handleCreateSubmit} onClose={() => setShowCreateModal(false)} loading={loading}
        />
      )}

      {showVisibilityModal && (
        <VisibilityModal
          t={t} styles={styles} theme={theme}
          allCompanies={allCompanies}
          loading={visibilityHook.loading}
          visibilityHook={visibilityHook}
          onClose={() => setShowVisibilityModal(false)}
          onSave={handleSaveVisibility}
        />
      )}

      {showAssignModal && (
        <AssignEmployeesModal
          t={t} styles={styles} theme={theme} language={language}
          assignHook={assignHook}
          adminLevels={adminLevels}
          onClose={() => setShowAssignModal(false)}
          onSave={handleSaveAssign}
        />
      )}

      {/* Delete Confirm */}
      <AlertModal
        open={showDeleteConfirm}
        title={t.deleteConfirm}
        message={t.deleteMessage}
        variant="error"
        showCancel={true}
        onConfirm={handleDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setDeletingLevelId(null); }}
        theme={theme}
      />
    </div>
  );
};

export default AdminLevelConfigurationPage;
